import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { getMeeting, updateMeeting, deleteMeeting, getTasks, getAgents, toggleMeetingVisibility } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Loader2,
  Edit2,
  Trash2,
  Save,
  CalendarClock,
  Clock,
  MapPin,
  Link as LinkIcon,
  ExternalLink,
  Mail,
  X,
  Eye,
  EyeOff,
  FileText,
  Users,
  ListTodo,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const statusColors = {
  'Planificada': 'bg-blue-100 text-blue-700 border-blue-200',
  'Convocada': 'bg-amber-100 text-amber-700 border-amber-200',
  'Realizada': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Cancelada': 'bg-slate-100 text-slate-500 border-slate-200',
};

const MeetingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canManageTasks, isSuperadmin, user } = useAuth();

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const [editForm, setEditForm] = useState({});
  const [allTasks, setAllTasks] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [taskSearch, setTaskSearch] = useState('');
  const [agentSearch, setAgentSearch] = useState('');

  // Email modal
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [emailBody, setEmailBody] = useState('');

  useEffect(() => {
    loadMeeting();
  }, [id]);

  const loadMeeting = async () => {
    try {
      const response = await getMeeting(id);
      setMeeting(response.data);
      setEditForm(response.data);
    } catch (error) {
      toast.error('Error al cargar la reunion');
      navigate('/meetings');
    } finally {
      setLoading(false);
    }
  };

  const loadEditData = async () => {
    try {
      const [tasksRes, agentsRes] = await Promise.all([
        getTasks({ sort_by: 'updated_at', sort_order: 'desc' }),
        getAgents()
      ]);
      setAllTasks(tasksRes.data);
      setAllAgents(agentsRes.data);
    } catch (error) {
      console.error('Error loading edit data:', error);
    }
  };

  const startEditing = async () => {
    setEditForm({ ...meeting });
    await loadEditData();
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {};
      const fields = ['title', 'date', 'time', 'location', 'meeting_link', 'notes', 'summary', 'status', 'task_ids', 'agent_ids'];
      for (const field of fields) {
        if (editForm[field] !== undefined) {
          updateData[field] = editForm[field];
        }
      }
      const response = await updateMeeting(id, updateData);
      setMeeting(response.data);
      setIsEditing(false);
      toast.success('Reunion actualizada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMeeting(id);
      toast.success('Reunion eliminada');
      navigate('/meetings');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await updateMeeting(id, { status: newStatus });
      setMeeting(response.data);
      toast.success(`Estado cambiado a ${newStatus}`);
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const toggleTask = (taskId) => {
    setEditForm(prev => ({
      ...prev,
      task_ids: (prev.task_ids || []).includes(taskId)
        ? prev.task_ids.filter(id => id !== taskId)
        : [...(prev.task_ids || []), taskId]
    }));
  };

  const toggleAgent = (agentId) => {
    setEditForm(prev => ({
      ...prev,
      agent_ids: (prev.agent_ids || []).includes(agentId)
        ? prev.agent_ids.filter(id => id !== agentId)
        : [...(prev.agent_ids || []), agentId]
    }));
  };

  // Reorder tasks in view mode (auto-save)
  const handleViewTaskReorder = async (result) => {
    if (!result.destination) return;
    const srcIdx = result.source.index;
    const destIdx = result.destination.index;
    if (srcIdx === destIdx) return;

    const reordered = Array.from(meeting.tasks_details);
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(destIdx, 0, moved);

    const newTaskIds = reordered.map(t => t.id);
    setMeeting(prev => ({ ...prev, tasks_details: reordered, task_ids: newTaskIds }));

    try {
      await updateMeeting(id, { task_ids: newTaskIds });
    } catch {
      toast.error('Error al guardar el orden');
      loadMeeting();
    }
  };

  // Reorder tasks in edit mode
  const handleEditTaskReorder = (result) => {
    if (!result.destination) return;
    const srcIdx = result.source.index;
    const destIdx = result.destination.index;
    if (srcIdx === destIdx) return;

    const reordered = Array.from(editForm.task_ids || []);
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(destIdx, 0, moved);
    setEditForm(prev => ({ ...prev, task_ids: reordered }));
  };

  // Email convocatoria
  const openEmailModal = () => {
    const agents = (meeting.agents_details || []).filter(a => a.email);
    setEmailRecipients(agents.map(a => ({ ...a, selected: true })));
    
    const APP_URL = window.location.origin;
    const tasksList = (meeting.tasks_details || [])
      .map((t, i) => `${i + 1}. ${t.sequential_number} - ${t.title} (${APP_URL}/tasks/${t.id})`)
      .join('\n');
    
    let body = `Reunion: ${meeting.title}\nFecha: ${meeting.date}`;
    if (meeting.time) body += ` a las ${meeting.time}`;
    if (meeting.location) body += `\nLugar: ${meeting.location}`;
    if (meeting.meeting_link) body += `\nEnlace: ${meeting.meeting_link}`;
    body += `\n\nTemas a tratar:\n${tasksList || '(sin temas asignados)'}`;
    if (meeting.notes) body += `\n\nNotas:\n${meeting.notes}`;
    
    setEmailBody(body);
    setShowEmailModal(true);
  };

  const sendEmail = () => {
    const to = emailRecipients.filter(r => r.selected).map(r => r.email).join(',');
    const subject = encodeURIComponent(`Reunion: ${meeting.title} - ${meeting.date}`);
    const body = encodeURIComponent(emailBody);
    window.open(`mailto:${to}?subject=${subject}&body=${body}`, '_self');
    setShowEmailModal(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  if (!meeting) return null;

  const filteredTasks = allTasks.filter(t =>
    t.title.toLowerCase().includes(taskSearch.toLowerCase()) ||
    t.sequential_number.includes(taskSearch)
  );
  const filteredAgents = allAgents.filter(a =>
    a.name.toLowerCase().includes(agentSearch.toLowerCase()) ||
    (a.company || '').toLowerCase().includes(agentSearch.toLowerCase())
  );

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="meeting-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
          <div className="flex items-start gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/meetings')} className="mt-1">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge className={`${statusColors[meeting.status]} text-xs font-bold uppercase border`}>
                  {meeting.status}
                </Badge>
              </div>
              <h1 className="text-2xl font-semibold text-slate-900 font-['Manrope']">
                {meeting.title}
              </h1>
            </div>
          </div>

          {canManageTasks() && (
            <div className="flex gap-2 flex-wrap">
              {meeting.agents_details?.some(a => a.email) && (
                <Button
                  variant="outline"
                  onClick={openEmailModal}
                  className="border-blue-200 text-blue-700 hover:bg-blue-50"
                  data-testid="email-convocatoria-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email Convocatoria
                </Button>
              )}
              <Button variant="outline" onClick={startEditing} className="border-stone-200" data-testid="edit-meeting-btn">
                <Edit2 className="w-4 h-4 mr-2" />
                Editar
              </Button>
              {isSuperadmin() && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await toggleMeetingVisibility(id);
                      setMeeting(prev => ({ ...prev, visible_to_departamentos: res.data.visible_to_departamentos }));
                      toast.success('Visibilidad actualizada');
                    } catch { toast.error('Error al cambiar visibilidad'); }
                  }}
                  className={meeting.visible_to_departamentos
                    ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    : "border-amber-200 text-amber-700 hover:bg-amber-50"}
                  data-testid="toggle-meeting-visibility-btn"
                >
                  {meeting.visible_to_departamentos ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                  {meeting.visible_to_departamentos ? 'Visible' : 'Oculta'}
                </Button>
              )}
              {isSuperadmin() && (
                <Button variant="outline" onClick={() => setShowDeleteDialog(true)} className="border-red-200 text-red-600 hover:bg-red-50" data-testid="delete-meeting-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Temas (Tasks) */}
            <Card className="bg-white border border-stone-200 shadow-sm">
              <CardHeader className="border-b border-stone-100 pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                  <ListTodo className="w-5 h-5 text-slate-600" />
                  Temas a Tratar ({meeting.tasks_details?.length || 0})
                  {meeting.tasks_details?.length > 1 && (
                    <span className="text-xs font-normal text-slate-400 ml-1">arrastra para reordenar</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {meeting.tasks_details?.length > 0 ? (
                  <DragDropContext onDragEnd={handleViewTaskReorder}>
                    <Droppable droppableId="view-tasks">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="divide-y divide-stone-100">
                          {meeting.tasks_details.map((task, index) => (
                            <Draggable key={task.id} draggableId={task.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`flex items-center gap-2 p-4 transition-colors ${
                                    snapshot.isDragging ? 'bg-amber-50 shadow-lg rounded-lg border border-amber-200' : 'hover:bg-stone-50'
                                  }`}
                                  data-testid={`task-item-${index}`}
                                >
                                  <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-stone-100" data-testid={`drag-handle-${index}`}>
                                    <GripVertical className="w-4 h-4 text-slate-300" />
                                  </div>
                                  <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0">
                                    {index + 1}
                                  </span>
                                  <Link
                                    to={`/tasks/${task.id}`}
                                    className="flex items-center gap-3 flex-1 min-w-0"
                                    onClick={(e) => { if (snapshot.isDragging) e.preventDefault(); }}
                                  >
                                    <span className="font-mono text-xs text-slate-500">{task.sequential_number}</span>
                                    <span className="text-sm text-slate-900 flex-1 truncate">{task.title}</span>
                                    <Badge className="text-[10px] uppercase border bg-slate-100 text-slate-500 border-slate-200 shrink-0">
                                      {task.status}
                                    </Badge>
                                    <ExternalLink className="w-4 h-4 text-slate-300 shrink-0" />
                                  </Link>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                ) : (
                  <div className="p-8 text-center text-slate-500">Sin temas asignados</div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            {meeting.notes && (
              <Card className="bg-white border border-stone-200 shadow-sm">
                <CardHeader className="border-b border-stone-100 pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                    <FileText className="w-5 h-5 text-slate-600" />
                    Notas
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-slate-600 whitespace-pre-wrap">{meeting.notes}</p>
                </CardContent>
              </Card>
            )}

            {/* Summary */}
            <Card className="bg-white border border-stone-200 shadow-sm">
              <CardHeader className="border-b border-stone-100 pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  Resumen de la Reunion
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {meeting.summary ? (
                  <p className="text-slate-600 whitespace-pre-wrap">{meeting.summary}</p>
                ) : (
                  <p className="text-sm text-slate-400 italic">Sin resumen. Edita la reunion para agregar el resumen tras realizarla.</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Info */}
            <Card className="bg-white border border-stone-200 shadow-sm">
              <CardHeader className="border-b border-stone-100 pb-4">
                <CardTitle className="text-lg font-semibold text-slate-900">Informacion</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                {/* Status change */}
                {canManageTasks() && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Estado</p>
                    <Select value={meeting.status} onValueChange={handleStatusChange}>
                      <SelectTrigger className="border-stone-200" data-testid="meeting-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Planificada">Planificada</SelectItem>
                        <SelectItem value="Convocada">Convocada</SelectItem>
                        <SelectItem value="Realizada">Realizada</SelectItem>
                        <SelectItem value="Cancelada">Cancelada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Fecha</p>
                  <p className="text-slate-900 flex items-center gap-2">
                    <CalendarClock className="w-4 h-4 text-slate-400" />
                    {meeting.date}
                    {meeting.time && (
                      <span className="flex items-center gap-1 text-slate-500">
                        <Clock className="w-4 h-4" />
                        {meeting.time}
                      </span>
                    )}
                  </p>
                </div>

                {meeting.location && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Lugar</p>
                    <p className="text-slate-900 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      {meeting.location}
                    </p>
                  </div>
                )}

                {meeting.meeting_link && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Enlace</p>
                    <a
                      href={meeting.meeting_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-2 text-sm break-all"
                      data-testid="meeting-link-url"
                    >
                      <LinkIcon className="w-4 h-4 shrink-0" />
                      Abrir reunion
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                  </div>
                )}

                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Creado por</p>
                  <p className="text-slate-900">{meeting.created_by_name}</p>
                </div>
              </CardContent>
            </Card>

            {/* Agents */}
            <Card className="bg-white border border-stone-200 shadow-sm">
              <CardHeader className="border-b border-stone-100 pb-4">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                  <Users className="w-5 h-5 text-slate-600" />
                  Agentes Convocados ({meeting.agents_details?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {meeting.agents_details?.length > 0 ? (
                  <div className="space-y-2">
                    {meeting.agents_details.map(agent => (
                      <div key={agent.id} className="flex items-center gap-2 text-sm">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                          <Users className="w-3 h-3 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-slate-900 truncate">{agent.name}</p>
                          {agent.email && <p className="text-xs text-slate-400 truncate">{agent.email}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Sin agentes convocados</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Reunion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Titulo</Label>
              <Input value={editForm.title || ''} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="edit-meeting-title" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input type="date" value={editForm.date || ''} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Hora</Label>
                <Input type="time" value={editForm.time || ''} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Lugar</Label>
                <Input value={editForm.location || ''} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Enlace reunion</Label>
              <Input value={editForm.meeting_link || ''} onChange={(e) => setEditForm({ ...editForm, meeting_link: e.target.value })} placeholder="https://teams.microsoft.com/..." />
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Resumen de la reunion</Label>
              <Textarea value={editForm.summary || ''} onChange={(e) => setEditForm({ ...editForm, summary: e.target.value })} rows={5} placeholder="Pega aqui el resumen de la reunion..." data-testid="edit-meeting-summary" />
            </div>

            {/* Tasks */}
            <div className="space-y-2">
              <Label>Temas (Tareas) - {(editForm.task_ids || []).length} sel.</Label>
              {(editForm.task_ids || []).length > 0 && (
                <DragDropContext onDragEnd={handleEditTaskReorder}>
                  <Droppable droppableId="edit-tasks" direction="vertical">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-1 mb-2">
                        {(editForm.task_ids || []).map((tid, idx) => {
                          const t = allTasks.find(t => t.id === tid);
                          if (!t) return null;
                          return (
                            <Draggable key={tid} draggableId={`edit-${tid}`} index={idx}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`flex items-center gap-2 px-2 py-1.5 rounded border text-sm ${
                                    snapshot.isDragging
                                      ? 'bg-amber-50 border-amber-200 shadow-md'
                                      : 'bg-emerald-50 border-emerald-200'
                                  }`}
                                >
                                  <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                                    <GripVertical className="w-3.5 h-3.5 text-slate-400" />
                                  </div>
                                  <span className="text-xs font-bold text-slate-500 w-4">{idx + 1}</span>
                                  <span className="font-mono text-xs text-emerald-600">{t.sequential_number}</span>
                                  <span className="text-emerald-800 truncate flex-1">{t.title}</span>
                                  <X className="w-3.5 h-3.5 cursor-pointer text-slate-400 hover:text-red-500 shrink-0" onClick={() => toggleTask(tid)} />
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
              <Input placeholder="Buscar tareas..." value={taskSearch} onChange={(e) => setTaskSearch(e.target.value)} className="mb-2" />
              <div className="max-h-36 overflow-y-auto border rounded p-1 space-y-0.5">
                {filteredTasks.map(task => (
                  <label key={task.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-stone-50 cursor-pointer text-sm">
                    <input type="checkbox" checked={(editForm.task_ids || []).includes(task.id)} onChange={() => toggleTask(task.id)} className="rounded border-stone-300" />
                    <span className="font-mono text-xs text-slate-500">{task.sequential_number}</span>
                    <span className="truncate">{task.title}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Agents */}
            <div className="space-y-2">
              <Label>Agentes - {(editForm.agent_ids || []).length} sel.</Label>
              <Input placeholder="Buscar agentes..." value={agentSearch} onChange={(e) => setAgentSearch(e.target.value)} className="mb-2" />
              {(editForm.agent_ids || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {(editForm.agent_ids || []).map(aid => {
                    const a = allAgents.find(a => a.id === aid);
                    return a ? (
                      <span key={aid} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded border border-blue-200">
                        {a.name}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => toggleAgent(aid)} />
                      </span>
                    ) : null;
                  })}
                </div>
              )}
              <div className="max-h-36 overflow-y-auto border rounded p-1 space-y-0.5">
                {filteredAgents.map(agent => (
                  <label key={agent.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-stone-50 cursor-pointer text-sm">
                    <input type="checkbox" checked={(editForm.agent_ids || []).includes(agent.id)} onChange={() => toggleAgent(agent.id)} className="rounded border-stone-300" />
                    <span>{agent.name}</span>
                    {agent.company && <span className="text-xs text-slate-400">({agent.company})</span>}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditing(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Convocatoria Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Email Convocatoria
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Asunto</Label>
              <p className="text-sm text-slate-800 mt-1 bg-stone-50 p-2 rounded border border-stone-200">
                Reunion: {meeting.title} - {meeting.date}
              </p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Destinatarios</Label>
              <div className="space-y-2">
                {emailRecipients.map((r, i) => (
                  <label key={r.id} className="flex items-center gap-3 p-2 rounded border border-stone-200 hover:bg-stone-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={r.selected}
                      onChange={() => setEmailRecipients(prev => prev.map((rec, idx) => idx === i ? { ...rec, selected: !rec.selected } : rec))}
                      className="rounded border-stone-300"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{r.name}</p>
                      <p className="text-xs text-slate-500 truncate">{r.email}</p>
                    </div>
                    <X className="w-4 h-4 text-slate-400 hover:text-red-500 cursor-pointer shrink-0" onClick={(e) => { e.preventDefault(); setEmailRecipients(prev => prev.filter((_, idx) => idx !== i)); }} />
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Mensaje</Label>
              <Textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={10}
                className="resize-y font-mono text-sm"
                data-testid="email-convocatoria-body"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>Cancelar</Button>
            <Button onClick={sendEmail} disabled={!emailRecipients.some(r => r.selected)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-convocatoria-btn">
              <Mail className="w-4 h-4 mr-2" />
              Abrir Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Reunion</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion no se puede deshacer. Se eliminara permanentemente la reunion <strong>{meeting.title}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default MeetingDetailPage;
